import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  clearSuggestionDetails,
  selectData,
  selectIsLoading,
  selectSelectedSuggestion,
  selectSuggestionDetails,
  selectDetailsLoading,
  selectResponseType,
  fetchData,
  selectSuggestionsLoading,
  setSuggestions,
  selectSuggestionsError,
  resetState,
  setChatbotId,
  selectUser,
  selectTilesDetails,
  selectRestrictedDataError,
  fetchSuggestionDetails,
  fetchSuggestions,
  selectDietPlanContext,
  setError,
  clearSuggestions,
  selectOpenAiTokens
} from "../../../../mainApp/features/DataSlice/dataSlice";
import {
  ChatbotContainer,
  ChatbotMain,
  ChatbotDetailsPanel,
  ChatbotForm,
  ChatbotButton,
  ChatbotExpandButton,
  ChatbotDetailsHeader,
  ChatbotError,
  ChatbotSuggestions,
  SuggestionsHeader,
  SuggestionsBox,
  SuggestionItem,
  SuggestionTitle,
  SuggestionDescription,
  ChatbotDetails,
  ChatbotMessage,
  AppTitle,
  LoadingElement,
  ChatbotTextarea,
  ChatbotInput,
  DocumentTypeSelector,
   NumberInputWrapper,
  UnitLabel,
  ChatbotNumberInput,
} from "../styled";
import LoadingPage from "../../TechnicalTabs/LoadingPage";
import ErrorPage from "../../../../mainApp/features/TechnicalTabs/ErrorPage";
import { auth, db } from "../../firebase";
import { doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { handleUnauthorizedAccess } from "../../Utility/handleUnauthorizedAccess";
import ReactMarkdown from 'react-markdown';
import { DIET_SUGGESTIONS_COST, DIET_DETAILS_COST } from "../../Utility/tokenCosts";
import DietDisclaimerModal from "./DietDisclaimerModal";
import { FaInfoCircle } from "react-icons/fa";
import styled from "styled-components";

const TitleContainer = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  justify-content: center;
`;

const InfoIcon = styled.div`
  color: ${({ theme }) => theme.color.spotifyGrey || "#6e7e91"};
  font-size: 20px;
  cursor: pointer;
  transition: color 0.2s ease;
  display: flex;
  align-items: center;
  line-height: 1;
  margin-top: 2px;
  
  &:hover {
    color: ${({ theme }) => theme.color.spotifyWhite || "#ffffff"};
  }
`;

const DietAdvisor = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoading = useSelector(selectIsLoading);
  const user = useSelector(selectUser);
  const tilesDetails = useSelector(selectTilesDetails);
  const restrictedDataError = useSelector(selectRestrictedDataError);
  const suggestionsLoading = useSelector(selectSuggestionsLoading);
  const suggestionsError = useSelector(selectSuggestionsError);
  const suggestions = useSelector(selectData);
  const selectedSuggestion = useSelector(selectSelectedSuggestion);
  const suggestionDetails = useSelector(selectSuggestionDetails);
  const detailsLoading = useSelector(selectDetailsLoading);
  const responseType = useSelector(selectResponseType);
  const dietPlanContext = useSelector(selectDietPlanContext);
  const currentTokens = useSelector(selectOpenAiTokens);

  const [weight, setWeight] = useState("");
  const [height, setHeight] = useState("");
  const [goal, setGoal] = useState("");
  const [likedProducts, setLikedProducts] = useState("");
  const [dietType, setDietType] = useState("");
  const [gender, setGender] = useState("");

  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [openAiTokens, setOpenAiTokensState] = useState(0);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);

  const validationToastId = 'diet-advisor-validation-toast';

  const goals = [
    { value: "lose-weight", label: "Schudnąć" },
    { value: "maintain-weight", label: "Utrzymać wagę" },
    { value: "gain-muscle", label: "Zbudować masę mięśniową" },
    { value: "healthy-lifestyle", label: "Zdrowy styl życia" },
  ];

  const dietTypes = [
    { value: "none", label: "Brak preferencji" },
    { value: "vegetarian", label: "Wegetariańska" },
    { value: "vegan", label: "Wegańska" },
    { value: "keto", label: "Ketogeniczna" },
    { value: "dash", label: "DASH" },
    { value: "mediterranean", label: "Śródziemnomorska" },
  ];

  const genders = [
    { value: "male", label: "Mężczyzna" },
    { value: "female", label: "Kobieta" },
  ];

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  useEffect(() => {
    dispatch(setChatbotId("diet"));
    dispatch(clearSuggestionDetails());
    dispatch(resetState());
    dispatch(fetchData());
    dispatch({ type: "FETCH_LAST_DIET_PLAN" });
    // Pokaż modal tylko jeśli użytkownik jeszcze nie zaakceptował
    const hasAcceptedDisclaimer = localStorage.getItem('dietDisclaimerAccepted');
    if (!hasAcceptedDisclaimer) {
      setShowDisclaimerModal(true);
    }

    return () => {
      dispatch(resetState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (dietPlanContext) {
      setWeight(dietPlanContext.weight || "");
      setHeight(dietPlanContext.height || "");
      setGoal(dietPlanContext.goal || "");
      setLikedProducts(dietPlanContext.likedProducts || "");
      setDietType(dietPlanContext.dietType || "");
      setGender(dietPlanContext.gender || "");
    }
  }, [dietPlanContext]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          // dispatch(fetchData());
          const ref = doc(db, "Users", currentUser.uid);
          const snap = await getDoc(ref);
          if (!snap.exists()) {
            console.warn("Dokument użytkownika nie istnieje.");
          }
          await delay(600);
        } catch (error) {
          console.error("Błąd pobierania danych użytkownika:", error);
          await delay(600);
        }
      } else {
        navigate("/login");
      }
      setInitialLoading(false);
    });

    return () => unsubscribe();
  }, [dispatch, navigate]);

  useEffect(() => {
    if (restrictedDataError) {
      navigate("/unauthorized");
    }
  }, [restrictedDataError, navigate]);

  useEffect(() => {
    if (currentTokens !== openAiTokens) {
      setOpenAiTokensState(currentTokens);
    }
  }, [currentTokens]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!weight || !height || !goal || !likedProducts || !dietType || !gender) {
      dispatch(
        setSuggestions(
          "Proszę wypełnić wszystkie pola, w tym wybrać cel, rodzaj diety oraz płeć."
        )
      );
      return;
    }

    const weightNum = parseInt(weight, 10);
    const heightNum = parseInt(height, 10);

    if (
      isNaN(weightNum) ||
      isNaN(heightNum) ||
      weightNum < 40 ||
      weightNum > 320 ||
      heightNum < 130 ||
      heightNum > 240
    ) {
      dispatch(
        setSuggestions(
          "Podaj odpowiednią wartość wagi i wzrostu dla osoby dorosłej."
        )
      );
      return;
    }

    const toastOptions = {
      position: "top-center",
      toastId: validationToastId,
    };

    if (currentTokens < DIET_SUGGESTIONS_COST) {
      toast.error("Masz za mało tokenów, aby wygenerować plan diety.", toastOptions);
      return;
    }

    dispatch(clearSuggestions());
    dispatch(fetchSuggestions());
    dispatch({
      type: "FETCH_DIET_SUGGESTIONS",
      payload: { weight, height, goal, likedProducts, dietType, gender },
    });
    dispatch(clearSuggestionDetails());
  };

  const handleSuggestionClick = (index) => {
    const toastOptions = {
      position: "top-center",
      toastId: validationToastId,
    };

    if (currentTokens < DIET_DETAILS_COST) {
      toast.error("Masz za mało tokenów, aby zobaczyć szczegóły jadłospisu.", toastOptions);
      return;
    }

    if (selectedSuggestion === index) {
      dispatch(clearSuggestionDetails());
    } else {
      if (!weight || !height || !goal || !gender) {
        dispatch(setError({
          context: "suggestions",
          message: "Nie można pobrać szczegółów. Ten plan został zapisany w starszej wersji i brakuje mu danych wejściowych (waga, wzrost, cel, płeć). Proszę wygenerować nowy plan — będzie on już działał w pełni poprawnie."
        }));
        dispatch(clearSuggestionDetails());
        return;
      }

      const suggestion = suggestions[index];
      dispatch(
        fetchSuggestionDetails({
          index,
          dayTitle: suggestion.title,
          dayDescription: suggestion.description,
          weight,
          height,
          goal,
          likedProducts: likedProducts || "brak preferencji",
          dietType,
          gender,
        })
      );
    }
  };

  const toggleDetailsPanel = () => setIsDetailsExpanded((prev) => !prev);

  if (initialLoading || isLoading === "loading") {
    return <LoadingPage />;
  }

  let returned;
  switch (isLoading) {
    case "success":
      if (!user) {
        returned = <LoadingPage />;
      } else if (tilesDetails.length > 0 && user) {
        returned = (
          <ChatbotContainer>
            <ChatbotMain
              isDetailsExpanded={isDetailsExpanded}
              isDetailsPanelVisible={selectedSuggestion !== null && suggestions.length > 0}
            >
              <TitleContainer>
                <AppTitle>Doradca Dietetyczny</AppTitle>
                <InfoIcon onClick={() => setShowDisclaimerModal(true)}>
                  <FaInfoCircle />
                </InfoIcon>
              </TitleContainer>

              <ChatbotForm onSubmit={handleSubmit} isDiet={true}>
                <div>
                  <DocumentTypeSelector
                    value={goal}
                    onChange={(e) => setGoal(e.target.value)}
                    style={{ flex: 2 }}
                    required
                  >
                    <option value="" disabled hidden>
                      Twój cel
                    </option>
                    {goals.map((g) => (
                      <option key={g.value} value={g.value}>
                        {g.label}
                      </option>
                    ))}
                  </DocumentTypeSelector>
                  <DocumentTypeSelector
                    value={dietType}
                    onChange={(e) => setDietType(e.target.value)}
                    style={{ flex: 2 }}
                    required
                  >
                    <option value="" disabled hidden>
                      Rodzaj diety
                    </option>
                    {dietTypes.map((d) => (
                      <option key={d.value} value={d.value}>
                        {d.label}
                      </option>
                    ))}
                  </DocumentTypeSelector>
                  <ChatbotButton type="submit" disabled={suggestionsLoading} style={{ flexShrink: 0 }} tokenCost={DIET_SUGGESTIONS_COST}>
                    Generuj Plan Diety
                  </ChatbotButton>
                </div>

                <div>
                  <NumberInputWrapper>
                    <ChatbotNumberInput
                      type="number"
                      value={weight}
                      onChange={(e) => setWeight(e.target.value)}
                      placeholder="Waga"
                      min="40"
                      max="320"
                      style={{ flex: 1 }}
                    />
                    <UnitLabel>kg</UnitLabel>
                  </NumberInputWrapper>
                  <NumberInputWrapper>
                    <ChatbotNumberInput
                      type="number"
                      value={height}
                      onChange={(e) => setHeight(e.target.value)}
                      placeholder="Wzrost"
                      min="130"
                      max="240"
                      style={{ flex: 1 }}
                    />
                    <UnitLabel>cm</UnitLabel>
                  </NumberInputWrapper>
                  <DocumentTypeSelector
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                    style={{ flex: 2 }}
                    required
                  >
                    <option value="" disabled hidden>
                      Płeć
                    </option>
                    {genders.map((g) => (
                      <option key={g.value} value={g.value}>
                        {g.label}
                      </option>
                    ))}
                  </DocumentTypeSelector>
                </div>

                <ChatbotTextarea
                  value={likedProducts}
                  onChange={(e) => setLikedProducts(e.target.value)}
                  placeholder="Wpisz produkty, które lubisz, oddzielając je przecinkami (np. kurczak, ryż, brokuły, oliwa z oliwek...)"
                />
              </ChatbotForm>

              {suggestionsLoading ? (
                <LoadingElement>Generowanie planu...</LoadingElement>
              ) : suggestionsError ? (
                <ChatbotError>{suggestionsError}</ChatbotError>
              ) : suggestions && responseType === "message" ? (
                <ChatbotMessage>{suggestions}</ChatbotMessage>
              ) : Array.isArray(suggestions) && suggestions.length > 0 && responseType === "suggestions" ? (
                <ChatbotSuggestions>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
                    <SuggestionsHeader style={{ margin: '0' }}>Proponowany plan na 10 dni:</SuggestionsHeader>
                    <span style={{ color: '#cd5c5c', fontSize: '0.8rem', fontStyle: 'italic', padding: '6px' }}>
                      Uwaga! Twój plan nie zniknie i możesz do niego wrócić, aż do momentu wygenerowania kolejnego planu.
                    </span>
                  </div>
                  <SuggestionsBox>
                    {suggestions.map((suggestion, index) => (
                      <SuggestionItem
                        key={index}
                        onClick={() => handleSuggestionClick(index)}
                        tokenCost={DIET_DETAILS_COST}
                      >
                        <SuggestionTitle>{suggestion.title}</SuggestionTitle>
                        <SuggestionDescription>{suggestion.description}</SuggestionDescription>
                      </SuggestionItem>
                    ))}
                  </SuggestionsBox>
                </ChatbotSuggestions>
              ) : (
                <ChatbotMessage>
                  Wypełnij formularz, aby otrzymać plan diety.
                </ChatbotMessage>
              )}
            </ChatbotMain>

            <ChatbotExpandButton
              onClick={toggleDetailsPanel}
              isDetailsExpanded={isDetailsExpanded}
            >
              {isDetailsExpanded ? "Zwiń szczegóły" : "Rozwiń szczegóły"}
            </ChatbotExpandButton>

            <ChatbotDetailsHeader isDetailsExpanded={isDetailsExpanded} />

            <ChatbotDetailsPanel isDetailsExpanded={isDetailsExpanded}>
              {selectedSuggestion !== null && suggestions.length > 0 ? (
                detailsLoading ? (
                  <LoadingElement>Generowanie szczegółów dnia...</LoadingElement>
                ) : suggestionDetails ? (
                  <ChatbotDetails isDiet>
                    <h2>{suggestions[selectedSuggestion].title}</h2>
                    <ReactMarkdown>{suggestionDetails}</ReactMarkdown>
                  </ChatbotDetails>
                ) : (
                  <ChatbotMessage>
                    Wystąpił błąd podczas ładowania szczegółów.
                  </ChatbotMessage>
                )
              ) : (
                <ChatbotMessage>
                  Wybierz dzień, aby zobaczyć szczegółowy jadłospis.
                </ChatbotMessage>
              )}
            </ChatbotDetailsPanel>
          </ChatbotContainer>
        );
      } else {
        returned = handleUnauthorizedAccess();
      }
      break;
    case "error":
      returned = <ErrorPage />;
      break;
    default:
      returned = <LoadingPage />;
  }

  const handleAcceptDisclaimer = () => {
    localStorage.setItem('dietDisclaimerAccepted', 'true');
    setShowDisclaimerModal(false);
  };

  return (
    <>
      {returned}
      <DietDisclaimerModal 
        show={showDisclaimerModal} 
        onAccept={handleAcceptDisclaimer} 
      />
    </>
  );
};

export default DietAdvisor; 